import { useState, useEffect, useRef, useCallback } from "react";

/**
 * useSessionDraft — persists a state value to localStorage so it survives
 * iOS PWA app-switch reloads.
 *
 * - On mount: reads from storage and restores if found
 * - On change: writes to storage (debounced ~300ms)
 * - visibilitychange → immediate flush (iOS kills before debounce)
 * - clearDraft() to remove after successful save
 */
export function useSessionDraft<T>(
  key: string,
  initial: T
): [T, React.Dispatch<React.SetStateAction<T>>, () => void] {
  // Read initial value from storage synchronously
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) {
        return JSON.parse(stored) as T;
      }
    } catch {
      // ignore parse errors
    }
    return initial;
  });

  const valueRef = useRef(value);
  valueRef.current = value;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flush current value to storage immediately
  const flush = useCallback(() => {
    try {
      const current = valueRef.current;
      // Don't store empty/null/default values to keep storage clean
      if (
        current === null ||
        current === undefined ||
        current === "" ||
        (Array.isArray(current) && current.length === 0)
      ) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(current));
      }
    } catch {
      // storage full or unavailable — fail silently
    }
  }, [key]);

  // Debounced write on value change
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(flush, 300);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [value, flush]);

  // Immediate flush on visibility change (app going to background)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        flush();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [flush]);

  // Clear draft from storage
  const clearDraft = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key]);

  return [value, setValue, clearDraft];
}
